import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, List, ListItem, styled } from '@mui/material';
import clsx from 'clsx';

import { ReactComponent as ChevronDownIcon } from 'assets/icons/ChevronDown.svg';

import { IAccountSettingItem } from 'interfaces/account';

import { Button } from 'components/_basic/Button/Button';

import { useAccountSettingsData } from './hooks/useAccountSettingsData';

import styles from './AccountSettings.module.scss';

import colors from 'styles/sass-variables.module.scss';

interface IProps {
  onClose: VoidFunction;
}

const StyledAccordion = styled(Accordion)(() => ({
  boxShadow: 'none',
  padding: 0,
}));

const StyledSummary = styled(AccordionSummary)(() => ({
  fontWeight: 700,
  lineHeight: 1.5,
  margin: 0,
  padding: 0,
  minHeight: '44px',
  color: colors.greys_black,

  '&.MuiAccordionSummary-expandIconWrapper': {
    color: `${colors.greys_black} !important`,
  },

  '&.Mui-expanded': {
    minHeight: '44px',
    color: colors.primary_purple_default,

    '&.MuiAccordionSummary-expandIconWrapper': {
      color: `${colors.primary_purple_default} !important`,
    },
  },

  '& .MuiAccordionSummary-content': {
    margin: 0,

    '&.Mui-expanded': {
      margin: 0,
    },
  },
}));

const StyledDetails = styled(AccordionDetails)(() => ({
  padding: 0,
}));

export const AccountSettingsMobile: FC<IProps> = ({ onClose }) => {
  const { menuContent, isLogOutPending } = useAccountSettingsData();
  const { t } = useTranslation();

  const { clickHandler: logOutHandler, title, icon } = menuContent[menuContent.length - 1];
  const dataToShow = menuContent.slice(0, -1);

  const logOutClickHandler = async () => {
    await logOutHandler();
    onClose();
  };

  return (
    <Box className={styles['root']}>
      <StyledAccordion defaultExpanded>
        <StyledSummary expandIcon={<ChevronDownIcon className={styles['chevron-icon']} />}>{t('myAccount')}</StyledSummary>
        <StyledDetails>
          <List className={styles['list']}>
            {dataToShow.map(({ title, clickHandler }: IAccountSettingItem) => (
              <ListItem className={styles['list-item']} key={title}>
                <Button
                  className={styles['button']}
                  onClick={() => {
                    clickHandler();
                    onClose();
                  }}
                  color="info"
                  variant="text"
                  fullWidth
                >
                  {title}
                </Button>
              </ListItem>
            ))}
          </List>
        </StyledDetails>
      </StyledAccordion>
      <Divider className={styles['divider']} />
      <Button
        className={clsx(styles['logout-button'], styles['button'])}
        endIcon={icon}
        onClick={logOutClickHandler}
        color="info"
        variant="text"
        isLoading={isLogOutPending}
        disabled={isLogOutPending}
        fullWidth
      >
        {title}
      </Button>
    </Box>
  );
};
